import { DataTableColumnHeader } from '@/components/admin/members/ColHeader'
import { Data } from '@/components/sudo/user/OrganizationsEditor'
import { CaseWorkerRole } from '@repo/shared/dtos/caseWorker'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/alert-dialog'
import { Button } from '@repo/ui/button'
import { Checkbox } from '@repo/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { useT } from '@repo/ui/hooks'
import { Cfl, cn } from '@repo/ui/utils'
import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal } from 'lucide-react'
import { useState } from 'react'

export const organizationsColumns: (
  joinOrLeaveOrganization: (
    action: 'join' | 'leave',
    organizationId: string,
  ) => void,
  updateCaseWorkerRole: (p: {
    caseWorkerId: string
    organizationId: string
    role: CaseWorkerRole
  }) => void,
) => ColumnDef<Data>[] = (joinOrLeaveOrganization, updateCaseWorkerRole) => [
  {
    id: 'join',
    header: ({ table }) => (
      <span className="select-none">
        {table.getFilteredSelectedRowModel().rows.length} organizations
      </span>
    ),
    cell: ({ row }) => {
      const { tc } = useT()
      const [showConfirm, setShowConfirm] = useState(false)
      const data = row.original
      const isSelected = row.getIsSelected()
      return (
        <>
          <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  You will grant the user access to the organization{' '}
                  <span className="font-medium">"{data.organizationName}"</span>
                  .
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    joinOrLeaveOrganization('join', data.id)
                    row.toggleSelected(true)
                  }}
                >
                  {tc('confirm')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => {
              if (isSelected) {
                joinOrLeaveOrganization('leave', data.id)
                row.toggleSelected(false)
              } else {
                setShowConfirm(true)
              }
            }}
            tabIndex={-1}
            aria-label="Join"
          />
        </>
      )
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'organizationName',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          options={{ sortable: true, searchable: true }}
        />
      )
    },
  },
  {
    accessorKey: 'caseWorkerId',
    header: () => {},
    cell: () => {},
    //TODO ALEXIS better way to hide it?
  },
  {
    accessorKey: 'firstName',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader title="Case Worker: FirstName" column={column} />
      )
    },
  },
  {
    accessorKey: 'lastName',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader title="Case Worker: LastName" column={column} />
      )
    },
  },
  {
    accessorKey: 'role',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          options={{ sortable: true, filterable: ['admin', 'case worker'] }}
        />
      )
    },
    cell: ({ row, column }) => {
      const value = row.getValue<CaseWorkerRole>(column.id)
      function RoleMenu({
        role,
        onSubmit,
      }: {
        role: string
        onSubmit: () => void
      }) {
        const isAdmin = role === 'admin'
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className={cn(isAdmin && 'text-highlight')}>
                  {Cfl(role)}
                </span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="select-none">
                <div className="cursor-pointer font-normal" onClick={onSubmit}>
                  {isAdmin ? 'Downgrade to case worker' : 'Increase to admin'}
                </div>
              </DropdownMenuLabel>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      }
      return (
        <>
          {value ? (
            <RoleMenu
              role={value}
              onSubmit={function () {
                const organizationId = row.id
                const caseWorkerId = row.getValue<string>('caseWorkerId')
                updateCaseWorkerRole({
                  caseWorkerId,
                  organizationId,
                  role:
                    value === CaseWorkerRole.Admin
                      ? CaseWorkerRole.CaseWorker
                      : CaseWorkerRole.Admin,
                })
              }}
            />
          ) : (
            ''
          )}
        </>
      )
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          options={{
            sortable: true,
            filterable: ['pending', 'enabled', 'disabled'],
          }}
        />
      )
    },
    cell: ({ row, column }) => {
      const value = row.getValue<string>(column.id)
      return (
        <>
          {value ? (
            <div
              className={cn(
                value === 'pending' && 'text-orange-500',
                value === 'disabled' && 'opacity-50',
              )}
            >
              {Cfl(value)}
            </div>
          ) : (
            ''
          )}
        </>
      )
    },
  },
]
